// Pipeline puro do encaminhamento, usado pelos 3 caminhos do bot (relay grammY,
// MTProto, WhatsApp) e pelo simulador de filtros do painel.
//
// Ordem: termos a ignorar (apaga linhas) -> sobrou link valido? -> filtro por
// valor -> janela semanal. A conversao de link acontece DEPOIS, no bot; o texto
// final (template ou texto original convertido) sai de composeOutgoing.
import { applyIgnoredTerms } from "./terms.js";
import { extractOffer } from "./offer.js";
import { evaluateValueFilter } from "./value-filter.js";
import { evaluateWindow } from "./window.js";
import { buildTemplateVars, escapeHtml, renderTemplate } from "./template.js";
import { findPreset, PRESET_PREFIX, resolveTemplateBody, templateRefFor, } from "./presets.js";
const URL_RE = /\bhttps?:\/\/[^\s<>"'`]+/giu;
const TRAILING_PUNCTUATION = /[)\].,;:!?]+$/u;
/** URLs http(s) do texto, na ordem (mesma regra do bot: relay/text.ts). */
export function extractUrls(text) {
    return [...text.matchAll(URL_RE)]
        .map((m) => m[0].replace(TRAILING_PUNCTUATION, ""))
        .filter((url) => {
        try {
            const parsed = new URL(url);
            return parsed.protocol === "http:" || parsed.protocol === "https:";
        }
        catch {
            return false;
        }
    });
}
export function planForward(input) {
    const { text, removed } = applyIgnoredTerms(input.text, input.config.ignoredTerms);
    const remaining = new Set(extractUrls(text));
    const accepted = (input.acceptedUrls ?? extractUrls(input.text)).filter((url) => remaining.has(url));
    const offer = extractOffer(text);
    const base = { text, removed, offer };
    if (accepted.length === 0) {
        return { ...base, action: "drop", reason: "no_link_after_terms" };
    }
    const value = evaluateValueFilter(input.config.valueFilter, offer);
    if (!value.pass) {
        return { ...base, action: "drop", reason: value.reason };
    }
    if (input.ignoreWindow === true) {
        return { ...base, action: "send", acceptedUrls: accepted };
    }
    const window = evaluateWindow(input.config.settings.window, input.now);
    if (window.open) {
        return { ...base, action: "send", acceptedUrls: accepted };
    }
    if (window.action === "drop") {
        return { ...base, action: "drop", reason: "outside_window" };
    }
    return { ...base, action: "queue", releaseAt: window.releaseAt, acceptedUrls: accepted };
}
/** Ref que aponta para template inexistente (preset desconhecido ou apagado). */
function missingRef(ref, userTemplates) {
    if (ref === null)
        return null;
    if (ref.startsWith(PRESET_PREFIX)) {
        return findPreset(ref.slice(PRESET_PREFIX.length)) ? null : ref;
    }
    return userTemplates.some((t) => t.id === ref) ? null : ref;
}
/** Anexa no final as URLs que o texto renderizado nao contem (nunca envia sem link). */
function appendMissingUrls(text, urls, target) {
    const missing = [...new Set(urls)].filter((url) => {
        const shown = target === "telegram" ? escapeHtml(url) : url;
        return !text.includes(shown);
    });
    if (missing.length === 0)
        return text;
    const lines = missing.map((url) => (target === "telegram" ? escapeHtml(url) : url));
    return `${text}\n\n${lines.join("\n")}`.trim();
}
export function composeOutgoing(input) {
    const settings = input.config.settings;
    if (!settings.useTemplate) {
        return { text: input.convertedText, parseMode: null };
    }
    const ref = templateRefFor(input.providerCode, settings.defaultTemplateRef, settings.templateByProvider);
    const body = resolveTemplateBody(ref, input.userTemplates);
    const vars = buildTemplateVars(input.offer, input.affiliateUrl, input.providerCode);
    const rendered = renderTemplate(body, vars, input.target);
    const affiliate = input.affiliateUrl;
    const shownAffiliate = affiliate === null ? null : input.target === "telegram" ? escapeHtml(affiliate) : affiliate;
    const text = appendMissingUrls(rendered, [...(affiliate === null ? [] : [affiliate]), ...(input.extraUrls ?? [])], input.target);
    const missing = missingRef(ref, input.userTemplates);
    return {
        text,
        parseMode: input.target === "telegram" ? "HTML" : null,
        ...(shownAffiliate !== null && !rendered.includes(shownAffiliate)
            ? { linkAppended: true }
            : {}),
        ...(missing !== null ? { missingTemplateRef: missing } : {}),
    };
}
export const TELEGRAM_CAPTION_LIMIT = 1024;
function visibleText(message) {
    if (message.parseMode !== "HTML")
        return message.text;
    return message.text
        .replace(/<\/?(b|i|s|u)>/g, "")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&amp;/g, "&");
}
/**
 * Cabe a mensagem no limite de legenda do Telegram (1024 caracteres visiveis)
 * sem perder a foto nem o link: corta o texto com reticencias e garante as
 * URLs de afiliado no final. Ao cortar, a formatacao HTML e descartada (cortar
 * HTML no meio de uma tag faria o Telegram recusar a mensagem).
 */
export function fitCaption(message, keepUrls, max = TELEGRAM_CAPTION_LIMIT) {
    const visible = visibleText(message);
    if (visible.length <= max)
        return message;
    const urls = [...new Set(keepUrls)].filter((url) => visible.includes(url));
    const tail = urls.length > 0 ? `\n${urls.join("\n")}` : "";
    let body = visible;
    for (const url of urls)
        body = body.split(url).join("");
    const budget = max - tail.length - 1;
    if (budget <= 0) {
        return { text: tail.trim().slice(0, max), parseMode: null };
    }
    const head = body
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .slice(0, budget)
        .trimEnd();
    return { text: `${head}…${tail}`, parseMode: null };
}
//# sourceMappingURL=pipeline.js.map